/**
 * KnowledgeResultsPane — the right rail: the clinic's own monographs, documents and links.
 *
 * Reshaped from the old Knowledge base tab (86d3qa4fq). The groups used to be one SectionCard
 * each at page level; inside a pane card that would nest cards, which §10 forbids and §26
 * answers directly ("Nest cards inside cards → use Tabs size='small' or collapsible"). So the
 * groups are a ghost Collapse, open by default — group identity survives without either nesting
 * or reintroducing the tab switching this ticket removes.
 *
 * `unavailable` sources are a non-blocking notice, never an error. Presentational — the page
 * owns the search. All colour via --mg-* tokens.
 */

import React from 'react';
import { Typography, Alert, Collapse } from 'antd';
import { ReadOutlined } from '@ant-design/icons';

import { SectionCard, EmptyState, InlineLoading } from '../../components/shared';
import ResultRow from './ResultRow';
import type { KnowledgeGroup, KnowledgeResult, KnowledgeSearchResponse } from '../../types/research/ResearchTypes';

const { Text } = Typography;

export interface KnowledgeResultsPaneProps {
	query: string;
	data: KnowledgeSearchResponse | undefined;
	isLoading: boolean;
	isError: boolean;
	onOpen: (result: KnowledgeResult, group: KnowledgeGroup) => void;
	selectedId?: string | null;
}

const KnowledgeResultsPane: React.FC<KnowledgeResultsPaneProps> = ({
	query,
	data,
	isLoading,
	isError,
	onOpen,
	selectedId,
}) => {
	const groups = (data?.data ?? []).filter((g) => g.source_category === 'knowledge_base');
	const unavailable = data?.unavailable ?? [];
	const total = data?.total ?? 0;

	const body = (() => {
		if (!query) {
			return (
				<EmptyState
					icon={<ReadOutlined style={{ fontSize: 40, color: 'var(--mg-text-secondary)', opacity: 0.4 }} />}
					title="Search the knowledge base"
					description="Clinic monographs, documents and resource links."
				/>
			);
		}
		if (isLoading) return <InlineLoading message="Searching knowledge base…" />;
		if (isError) {
			return <EmptyState title="Knowledge search failed" description="Adjust your query and try again." />;
		}

		return (
			<>
				{unavailable.length > 0 && (
					<div style={{ padding: 'var(--mg-sp-4)' }}>
						<Alert
							type="info"
							showIcon
							message="Some sources are unavailable"
							description={`Could not search: ${unavailable.join(', ')}.`}
							data-testid="research-knowledge-unavailable"
						/>
					</div>
				)}

				{total === 0 || groups.length === 0 ? (
					<EmptyState title="No results" description="No knowledge-base entries match this query." />
				) : (
					<Collapse
						ghost
						size="small"
						defaultActiveKey={groups.map((g) => g.source_type)}
						data-testid="research-knowledge-groups"
						items={groups.map((g) => ({
							key: g.source_type,
							label: (
								<span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
									<Text style={{ fontSize: 'var(--mg-fs-md)', fontWeight: 600 }}>{g.name}</Text>
									<Text type="secondary" className="mg-mono-num" style={{ fontSize: 'var(--mg-fs-xs)' }}>
										{g.count}
									</Text>
								</span>
							),
							children: (
								<div style={{ display: 'flex', flexDirection: 'column' }}>
									{g.results.map((r) => (
										<ResultRow
											key={r.id}
											title={r.title}
											attribution={r.subtitle}
											snippet={r.snippet}
											selected={selectedId === r.id}
											onClick={() => onOpen(r, g)}
											data-testid={`research-knowledge-row-${r.id}`}
										/>
									))}
								</div>
							),
						}))}
					/>
				)}
			</>
		);
	})();

	return (
		<SectionCard
			title="Knowledge base"
			subtitle={query && !isLoading && !isError ? `${total} result${total === 1 ? '' : 's'}` : undefined}
			className="mg-research-pane mg-research-pane--flush"
		>
			{body}
		</SectionCard>
	);
};

export default KnowledgeResultsPane;
